// These are the pages you can go to.
// They are all wrapped in the App component, which should contain the navbar etc
// See http://blog.mxstbr.com/2016/01/react-apps-with-pages for more information
// about the code splitting business
import { browserHistory } from 'react-router';
import { sendLog, LoggerStatusEnum } from 'ui-model';

import getReducerInjectors from '@lib/utils/reducerInjectors';
import getSagaInjectors from '@lib/utils/sagaInjectors';
import { capitalizeFirstLetter } from '@lib/services/stringsService';

import App from 'containers/App';
import Main from 'containers/Main';

const errorLoading = (err) => {
  sendLog('Error on routes.js - Dynamic page loading failed', { errorStack: err }, LoggerStatusEnum.error);
  browserHistory.push('/notfound');
};

const loadModule = (cb) => (componentModule) => {
  cb(null, componentModule.default);
};

const rootRedirect = (nextState, replace) => {
  const {
    location: { pathname, query },
  } = nextState;
  if (pathname !== '/') {
    return;
  }
  replace({
    pathname: '/login',
    query,
  });
};

export function createRoutes(store) {
  // Create reusable async injectors using getAsyncInjectors factory
  const reducerInjectors = getReducerInjectors(store);
  const sagaInjectors = getSagaInjectors(store);

  const getComponentMain = (nextState, cb) => {
    // lookup nested component per page
    const {
      params: { page },
    } = nextState;
    const compName = `${capitalizeFirstLetter(page)}Page`;
    const importModules = Promise.all([
      import(`containers/${compName}/reducer`),
      import(`containers/${compName}/sagas`),
      import(`containers/${compName}/index`),
    ]);

    const renderRoute = loadModule(cb);

    importModules.then(([reducer, sagas, component]) => {
      reducerInjectors.injectReducer(capitalizeFirstLetter(compName), reducer.default);
      sagaInjectors.injectSaga(compName, { saga: sagas.default });
      renderRoute(component);
    });

    importModules.catch(errorLoading);
  };

  const internalRoutes = [
    {
      component: App,
      path: '/',
      onEnter: rootRedirect,
      childRoutes: [
        {
          path: '/login', // ui-next > ContainerAuthPatientLogin
          getComponent(nextState, cb) {
            const importModules = Promise.all([import('containers/LoginPage/sagas'), import('screens/nexty/Login')]);
            const renderRoute = loadModule(cb);
            importModules.then(([sagas, component]) => {
              sagaInjectors.injectSaga('LoginPage', { saga: sagas.default });
              renderRoute(component);
            });

            importModules.catch(errorLoading);
          },
        },
        {
          path: '/signup', // ui-next > ContainerAuthPatientLogin
          getComponent(nextState, cb) {
            const importModules = Promise.all([
              import('containers/SignupPage/reducer'),
              import('containers/SignupPage/sagas'),
              import('containers/SignupPage'),
            ]);
            const renderRoute = loadModule(cb);
            importModules.then(([reducer, sagas, component]) => {
              reducerInjectors.injectReducer('SignupPage', reducer.default);
              sagaInjectors.injectSaga('SignupPage', { saga: sagas.default });
              renderRoute(component);
            });

            importModules.catch(errorLoading);
          },
        },
        {
          path: '/verify', // ui-next > ContainerAuthPatientLogin
          getComponent(nextState, cb) {
            const importModules = Promise.all([
              import('containers/VerifyUserPage/reducer'),
              import('containers/VerifyUserPage/sagas'),
              import('containers/VerifyUserPage'),
            ]);
            const renderRoute = loadModule(cb);
            importModules.then(([reducer, sagas, component]) => {
              reducerInjectors.injectReducer('VerifyUserPage', reducer.default);
              sagaInjectors.injectSaga('VerifyUserPage', { saga: sagas.default });
              renderRoute(component);
            });

            importModules.catch(errorLoading);
          },
        },
        {
          path: '/main',
          component: Main,
          childRoutes: [
            {
              path: '/main/:page',
              getComponent: getComponentMain,
            },
          ],
        },
        {
          path: '/availabilities',
          getComponent(nextState, cb) {
            import('screens/nexty/Availabilities')
              .then(loadModule(cb))
              .catch(errorLoading);
          },
        },
        {
          path: '/booked-availability',
          getComponent(nextState, cb) {
            import('screens/BookedAvailability')
              .then(loadModule(cb))
              .catch(errorLoading);
          },
        },
        {
          path: '/reschedule/availabilities',
          getComponent(nextState, cb) {
            import('screens/nexty/Availabilities')
              .then(loadModule(cb))
              .catch(errorLoading);
          },
        },
        {
          path: '/insurance',
          getComponent(nextState, cb) {
            const importModules = Promise.all([
              import('core/InsurancePage/reducer'),
              import('core/InsurancePage/sagas'),
              import('core/InsurancePage'),
            ]);
            const renderRoute = loadModule(cb);
            importModules.then(([reducer, sagas, component]) => {
              reducerInjectors.injectReducer('InsurancePage', reducer.default);
              sagaInjectors.injectSaga('InsurancePage', { saga: sagas.default });
              renderRoute(component);
            });
            importModules.catch(errorLoading);
          },
        },
        {
          path: '/reschedule/waitlist',
          getComponent(nextState, cb) {
            const importModules = Promise.all([
              import('containers/RescheduleWaitlistPage/reducer'),
              import('containers/RescheduleWaitlistPage/sagas'),
              import('containers/RescheduleWaitlistPage'),
            ]);
            const renderRoute = loadModule(cb);
            importModules.then(([reducer, sagas, component]) => {
              reducerInjectors.injectReducer('RescheduleWaitlistPage', reducer.default);
              sagaInjectors.injectSaga('RescheduleWaitlistPage', { saga: sagas.default });
              renderRoute(component);
            });

            importModules.catch(errorLoading);
          },
        },
        {
          path: '/reschedule/waitlist/success',
          getComponent(nextState, cb) {
            import('containers/RescheduleWaitlistSuccessPage')
              .then(loadModule(cb))
              .catch(errorLoading);
          },
        },
        {
          path: '/offers/reply',
          getComponent(nextState, cb) {
            const importModules = Promise.all([
              import('containers/OffersReplyPage/reducer'),
              import('containers/OffersReplyPage/sagas'),
              import('containers/OffersReplyPage'),
            ]);
            const renderRoute = loadModule(cb);
            importModules.then(([reducer, sagas, component]) => {
              reducerInjectors.injectReducer('OffersReplyPage', reducer.default);
              sagaInjectors.injectSaga('OffersReplyPage', { saga: sagas.default });
              renderRoute(component);
            });

            importModules.catch(errorLoading);
          },
        },
        {
          path: '/reminders/reply',
          getComponent(nextState, cb) {
            const importModules = Promise.all([
              import('containers/RemindersReplyPage/reducer'),
              import('containers/RemindersReplyPage/sagas'),
              import('containers/RemindersReplyPage'),
            ]);
            const renderRoute = loadModule(cb);
            importModules.then(([reducer, sagas, component]) => {
              reducerInjectors.injectReducer('RemindersReplyPage', reducer.default);
              sagaInjectors.injectSaga('RemindersReplyPage', { saga: sagas.default });
              renderRoute(component);
            });

            importModules.catch(errorLoading);
          },
        },
        {
          path: '/appointment/reply',
          getComponent(nextState, cb) {
            const importModules = Promise.all([
              import('containers/AppointmentReplyPage/reducer'),
              import('containers/AppointmentReplyPage/sagas'),
              import('containers/AppointmentReplyPage'),
            ]);
            const renderRoute = loadModule(cb);
            importModules.then(([reducer, sagas, component]) => {
              reducerInjectors.injectReducer('AppointmentReplyPage', reducer.default);
              sagaInjectors.injectSaga('AppointmentReplyPage', { saga: sagas.default });
              renderRoute(component);
            });

            importModules.catch(errorLoading);
          },
        },
        {
          path: '/book',
          getComponent(nextState, cb) {
            import('screens/nexty/Book')
              .then(loadModule(cb))
              .catch(errorLoading);
          },
        },
        {
          path: '/reschedule/book',
          getComponent(nextState, cb) {
            import('screens/BookScreen')
              .then(loadModule(cb))
              .catch(errorLoading);
          },
        },
        {
          path: '/waitingroomspatients/join',
          getComponent(nextState, cb) {
            const importModules = Promise.all([
              import('containers/JoinWaitingRoomPage/reducer'),
              import('containers/JoinWaitingRoomPage/sagas'),
              import('containers/JoinWaitingRoomPage'),
            ]);
            const renderRoute = loadModule(cb);
            importModules.then(([reducer, sagas, component]) => {
              reducerInjectors.injectReducer('JoinWaitingRoomPage', reducer.default);
              sagaInjectors.injectSaga('JoinWaitingRoomPage', { saga: sagas.default });
              renderRoute(component);
            });

            importModules.catch(errorLoading);
          },
        },
        {
          path: '/waitlists/:id',
          getComponent(nextState, cb) {
            import('screens/WaitlistsScreen')
              .then(loadModule(cb))
              .catch(errorLoading);
          },
        },
        {
          path: '/feedback/score/:id',
          getComponent(nextState, cb) {
            const importModules = Promise.all([
              import('containers/FeedbackPage/reducer'),
              import('containers/FeedbackPage/sagas'),
              import('containers/FeedbackPage'),
            ]);

            const renderRoute = loadModule(cb);

            importModules.then(([reducer, sagas, component]) => {
              reducerInjectors.injectReducer('FeedbackPage', reducer.default);
              sagaInjectors.injectSaga('FeedbackPage', { saga: sagas.default });
              renderRoute(component);
            });

            importModules.catch(errorLoading);
          },
        },
        {
          path: '/unsubscribe',
          getComponent(nextState, cb) {
            import('containers/UnsubscribePage')
              .then(loadModule(cb))
              .catch(errorLoading);
          },
        },
        {
          path: '/form', // ui-next > ContainerPatientFormTemplateLaunch
          getComponent(nextState, cb) {
            import('screens/nexty/PatientForm')
              .then(loadModule(cb))
              .catch(errorLoading);
          },
        },
        {
          path: '/appointments/:id',
          getComponent(_, cb) {
            import('screens/AppointmentScreen')
              .then(loadModule(cb))
              .catch(errorLoading);
          },
        },
        {
          path: '/session-expired', // ui-next > DialogSessionIdle
          getComponent(nextState, cb) {
            import('screens/SessionExpiredScreen')
              .then(loadModule(cb))
              .catch(errorLoading);
          },
        },
        {
          path: '/survey', // ui-next > ContainerPatientFormTemplateLaunch
          getComponent(nextState, cb) {
            const importModules = Promise.all([
              import('containers/SignupPage/reducer'),
              import('containers/SignupPage/sagas'),
              import('screens/nexty/PatientForm'),
            ]);
            const renderRoute = loadModule(cb);
            importModules.then(([reducer, sagas, component]) => {
              reducerInjectors.injectReducer('SignupPage', reducer.default);
              sagaInjectors.injectSaga('SignupPage', { saga: sagas.default });
              renderRoute(component);
            });

            importModules.catch(errorLoading);
          },
        },
        {
          path: '/forms', // ui-next > ContainerPatientFormTemplateLaunch
          getComponent(nextState, cb) {
            const importModules = Promise.all([
              import('containers/SignupPage/reducer'),
              import('containers/SignupPage/sagas'),
              import('screens/CustomFormsScreen'),
            ]);
            const renderRoute = loadModule(cb);
            importModules.then(([reducer, sagas, component]) => {
              reducerInjectors.injectReducer('SignupPage', reducer.default);
              sagaInjectors.injectSaga('SignupPage', { saga: sagas.default });
              renderRoute(component);
            });

            importModules.catch(errorLoading);
          },
        },
        {
          path: '/v2/telehealth',
          getComponent(nextState, cb) {
            import('screens/TelehealthScreen')
              .then(loadModule(cb))
              .catch(errorLoading);
          },
        },
        {
          path: '/checklist', // ui-next > ContainerChecklistLaunch
          getComponent(nextState, cb) {
            import('screens/nexty/Checklist')
              .then(loadModule(cb))
              .catch(errorLoading);
          },
        },
        {
          path: '/telehealth',
          getComponent(nextState, cb) {
            import('screens/TelehealthScreen')
              .then(loadModule(cb))
              .catch(errorLoading);
          },
        },
        {
          path: '/v2/telehealth/guest',
          getComponent(nextState, cb) {
            import('screens/TelehealthScreen')
              .then(loadModule(cb))
              .catch(errorLoading);
          },
        },
        {
          path: '/telehealth/guest',
          getComponent(nextState, cb) {
            import('screens/TelehealthScreen')
              .then(loadModule(cb))
              .catch(errorLoading);
          },
        },
        {
          path: '/hubpayment', // ui-next > ContainerPayment
          getComponent(nextState, cb) {
            import('screens/nexty/Payment')
              .then(loadModule(cb))
              .catch(errorLoading);
          },
        },
        {
          path: '/insurance-verification',
          getComponent(nextState, cb) {
            import('screens/InsuranceVerificationScreen')
              .then(loadModule(cb))
              .catch(errorLoading);
          },
        },
        {
          path: '/lumabot',
          getComponent(nextState, cb) {
            import('modules/LumaBot/LumaBotVersioning')
              .then(loadModule(cb))
              .catch(errorLoading);
          },
        },
        {
          path: '/settings',
          getComponent(nextState, cb) {
            import('modules/Settings')
              .then(loadModule(cb))
              .catch(errorLoading);
          },
        },
        {
          path: '/settings/campaigns',
          getComponent(nextState, cb) {
            import('modules/Settings/modules/Campaigns')
              .then(loadModule(cb))
              .catch(errorLoading);
          },
        },
        // NotFoundPage
        {
          path: '*',
          name: 'notfound',
          getComponent(nextState, cb) {
            import('core/NotFoundPage')
              .then(loadModule(cb))
              .catch(errorLoading);
          },
        },
      ],
    },
  ];

  return internalRoutes;
}
